import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import styled from 'styled-components';
import logoFull from '../assets/logos/DynamWorks-logofull.png';
import logoStill from '../assets/logos/dw-logo-3d-rest.webp';
import { WINDOW_MS, ARRIVAL_DEADLINE_MS } from './logoSpin';

// PERF: Lazy load Logo3D to avoid loading Three.js (~300KB) synchronously
const Logo3D = lazy(() => import('./Logo3D'));

const LogoContainer = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  margin: 0 auto;
  perspective: 1000px;
  cursor: pointer;

  @media (min-width: 768px) {
    position: absolute;
    top: 20px;
    left: 20px;
    margin: 0;
  }
`;

const LogoWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: transform 0.6s ease-in-out;
  transform-style: preserve-3d;

  &:hover {
    transform: scale(1.1);
  }
`;

const LogoFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FrontFace = styled(LogoFace)`
  transform: rotateY(0deg);
`;

const BackFace = styled(LogoFace)`
  transform: rotateY(180deg);
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

/**
 * The resting state of the front face: a still of the REAL 3D logo.
 *
 * 🔴 IT IS THE LOGO, NOT A LOADING BOX, AND THAT IS WHY IT CARRIES NO GLASS.
 * This used to be a tinted rounded box containing the letters `DW` — two
 * letters the 3D logo does not even draw (it draws a D and two Vs forming a
 * stylised W), inside a surface the canvas does not have. While the mount was
 * a race it was a flash nobody saw; DWEB-221 made the mount conditional on an
 * interaction, so it became the PERMANENT state for every visitor who never
 * touches the logo.
 *
 * The image is `scripts/captureLogoSnapshot.cjs`'s output — the real Logo3D at
 * its rest pose, same camera, same lighting, transparent. Because it is a
 * capture of the component rather than a second drawing of it, there is no
 * second declaration of the pose to drift: re-run the script after any change
 * to Logo3D.js and this follows.
 *
 * It is ABSOLUTE and sits over the canvas deliberately. Suspense resolving
 * means the MODULE arrived, not that WebGL has painted, so a fallback keyed on
 * Suspense uncovers an empty box. 🔴 And `onReady` is not that signal either —
 * measured, it precedes the first painted pixel by ~445 ms. This is keyed on
 * `onDrawn`, which Logo3D raises once its geometry exists (see Logo3D.js).
 */
const LogoStill = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

/** Any of these on the window mounts the 3D logo at once. */
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'];

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * DWEB-193 §1 / DWEB-221: WHEN the 3D logo mounts — ON INTERACTION, AND ONLY THEN.
 *
 * Logo3D is lazy, but it rendered unconditionally, so its chunks (three.js
 * `163`, 703,832 B; `@react-three/fiber` `510`, 181,134 B), the 64 KB
 * helvetiker glyph file, a WebGL context and drei's Text3D extrusion all
 * landed on the first screen. The logo sits top-left in the hero, so a
 * viewport gate would defer nothing.
 *
 * DWEB-193 deferred it to `load` + an idle callback, bounded by a 1,000 ms
 * timeout, OR the first interaction. 🔴 THE TIMEOUT MADE THE DEFERRAL A DELAY
 * RATHER THAN A GATE: with no click, hover, key or scroll, idle never came on a
 * throttled mobile run and the timeout fired anyway — measured requesting both
 * chunks at ~2,450 ms and costing 251 ms of evaluation (131 + 120) inside the
 * TBT window, whose end is TTI, so the whole cost was counted. DWEB-221.
 *
 * 🔴 AND DEFERRING FURTHER WOULD HAVE BOUGHT NOTHING — loading these chunks is
 * what MOVES TTI, so any schedule that fires unprompted lands inside the window
 * by construction. The only thing that removes the cost is not loading them for
 * a visitor who never asks. So the schedule is GONE; the interaction listeners
 * are untouched.
 *
 * What a non-interacting visitor now gets: the STILL of the 3D logo, which
 * holds the box. Nothing is traded away — the 3D logo they were
 * paying for SITS STILL until an interaction anyway, because the spin is driven
 * by the same events (DWEB-83) — so the still and the canvas show the same
 * picture, and 885 KB is the whole difference between them. (Until this release
 * the resting state was the letters `DW` in a glass box, which DWEB-221 turned
 * from a flash into the permanent state; see LogoStill.) Hover, click, tab, touch, wheel or scroll and
 * the 3D logo arrives exactly as before; `onClick` / `onPointerEnter` /
 * `onFocus` on the container call `mount3D()` directly, which is the keyboard
 * path and is asserted by `logo-mount.spec.js`.
 */
function useMountOnInteraction() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return undefined;
    const go = () => setReady(true);
    INTERACTION_EVENTS.forEach((e) => window.addEventListener(e, go, { passive: true }));
    return () => {
      INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, go));
    };
  }, [ready]);

  return [ready, () => setReady(true)];
}

const LogoAnimation = () => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [show3D, mount3D] = useMountOnInteraction();
  /**
   * DWEB-83: a monotonically increasing spin counter replaced the boolean.
   *
   * Each increment is one 360-degree spin, and Logo3D targets
   * `2π * spinCount`, so the rotation always moves FORWARD. The old boolean
   * animated back to zero when it cleared — a reverse spin — and it was driven
   * by a setInterval that fired every 10 seconds forever.
   */
  const [spinCount, setSpinCount] = useState(0);
  const [isInViewport, setIsInViewport] = useState(true);
  const isInViewportRef = useRef(true);
  /** DWEB-193: set once the 3D logo has drawn (Logo3D's onReady). */
  const readyRef = useRef(false);
  /**
   * The still yields to the canvas only once Logo3D reports PIXELS — `onDrawn`,
   * not `onReady` and certainly not Suspense resolving. Measured on the built
   * artifact: the canvas exists and is blank from ~455 ms to ~900 ms after a
   * cold hover, because Text3D fetches a glyph file and extrudes geometry after
   * the module lands. Keyed on `onReady` this handover uncovered an empty box,
   * which is what logo-still.spec.js reported on the first build of this change.
   */
  const [drawn, setDrawn] = useState(false);
  const logoContainerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isInViewportRef.current = entry.isIntersecting;
          setIsInViewport(entry.isIntersecting);
        });
      },
      {
        root: null,
        rootMargin: '50px',
        threshold: 0.1
      }
    );

    const currentRef = logoContainerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  /**
   * DWEB-83: ONE spin on arrival. The setInterval that used to sit here is gone.
   *
   * It fired every 10 seconds forever and each spin lasted 3, so the logo was in
   * motion roughly 30% of the time for as long as the tab stayed open. Even with
   * a demand-driven renderer that is a main thread which never gets a quiet
   * stretch, which is precisely the condition Lighthouse fails on.
   *
   * The reduced-motion check is kept and now decides whether the arrival spin
   * happens at all, rather than whether a permanent cycle starts.
   */
  const spin = useCallback(() => {
    if (prefersReducedMotion()) return;
    setSpinCount((n) => n + 1);
  }, []);

  /**
   * DWEB-193: the ARRIVAL spin is requested when the 3D logo reports it has
   * drawn, and only if it can finish by ARRIVAL_DEADLINE_MS on the page's
   * clock (logoSpin.js says why). A spin already requested by an interaction
   * that mounted the logo early is not doubled.
   */
  /** Logo3D has actually painted: the still can go. */
  const handleDrawn = useCallback(() => setDrawn(true), []);

  const handleReady = useCallback(() => {
    readyRef.current = true;
    if (!isInViewportRef.current || prefersReducedMotion()) return;
    if (performance.now() + WINDOW_MS > ARRIVAL_DEADLINE_MS) return;
    setSpinCount((n) => (n > 0 ? n : 1));
  }, []);

  useEffect(() => {
    if (!isInViewport || !readyRef.current) return;
    spin();
    // Deliberately keyed on RE-entering the viewport, NOT on an interval.
    // Scrolling away and back re-announces the logo; sitting on the page does
    // not. The first arrival is handleReady's, once the 3D logo has drawn.
  }, [isInViewport, spin]);

  const handleClick = () => {
    setIsFlipped(!isFlipped);
    // DWEB-83: the loop is bounded now, so an interaction has to ask for frames.
    spin();
  };

  const handleArrival = () => {
    mount3D();
    spin();
  };

  return (
    <LogoContainer
      ref={logoContainerRef}
      onClick={handleClick}
      onPointerEnter={handleArrival}
      onFocus={handleArrival}
    >
      <LogoWrapper style={{ transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}>
        <FrontFace data-logo-face="front">
          {/* DWEB-221: three.js is fetched only once show3D is set, which now happens
              ONLY on an interaction — the load+idle schedule is gone (its 1,000 ms
              timeout fired unprompted). isInViewport feeds the spin only.
              NOTE handleClick does NOT call mount3D: a click mounts through the
              window `pointerdown` listener, which fires first. handleArrival (hover,
              focus) calls it directly.

              🔴 THE STILL COVERS BOTH GAPS, WHICH IS WHY THERE IS ONE OF IT AND NOT
              TWO. It used to appear in two places — as the resting state AND as the
              Suspense fallback — so replacing only the resting one would have given a
              hover the sequence logo -> `DW` text -> 3D logo, a flash landing exactly
              when someone is looking at the logo on purpose. Here the still is a
              sibling that outlives Suspense entirely and is removed on `drawn`, so
              the box is never empty and the `DW` text is gone from both. */}
          {show3D && (
            <Suspense fallback={null}>
              <Logo3D spinCount={spinCount} onReady={handleReady} onDrawn={handleDrawn} />
            </Suspense>
          )}
          {!drawn && (
            <LogoStill src={logoStill} alt="DynamWorks" data-testid="logo-still" />
          )}
        </FrontFace>
        <BackFace>
          <LogoImage src={logoFull} alt="DynamWorks Full Logo" />
        </BackFace>
      </LogoWrapper>
    </LogoContainer>
  );
};

export default LogoAnimation;
